import React, { useState, useEffect, useContext } from 'react';
import { Card, Badge, Row, Col, Form, Button, Alert, Collapse } from 'react-bootstrap';
import { FaEdit, FaChevronDown, FaChevronUp, FaStar } from 'react-icons/fa';
import EditTaskModal from './EditTaskModal';
import userApi from '../services/userApi';
import { formatDate } from '../utils/dateFormatter';
import useLanguage from '../hooks/useLanguage';
import { AuthContext } from '../context/AuthContext';
import GuestCheckoutLine from './GuestCheckoutLine';
import GuestAssessmentSummary from './GuestAssessmentSummary';
import { taskRefusalKey } from '../utils/taskErrorCodes';
import { taskTypeBadge } from '../config/taskTypes';

const TaskCard = ({ task, onStatusUpdate, onAssign, onEdit }) => {
  const { t, isArabic, language } = useLanguage();
  const { user } = useContext(AuthContext);
  const canEditTask = user?.role !== 'maintenance';

  const PRIORITY_STYLES = {
    critical: { bg: 'danger', emoji: '🔴', text: 'text-danger', label: t('tasks.priority.label.critical') },
    urgent: { bg: 'warning', emoji: '🟠', text: 'text-warning', label: t('tasks.priority.label.urgent') },
    high: { bg: 'info', emoji: '🟡', text: 'text-info', label: t('tasks.priority.label.high') },
    medium: { bg: 'success', emoji: '🟢', text: 'text-success', label: t('tasks.priority.label.medium') },
    low: { bg: 'primary', emoji: '🔵', text: 'text-primary', label: t('tasks.priority.label.low') }
  };

  const STATUS_LABELS = {
    pending: t('tasks.status.pending'),
    assigned: t('tasks.status.assigned'),
    'in-progress': t('tasks.status.inProgress'),
    completed: t('tasks.status.completed'),
    cancelled: t('tasks.status.cancelled')
  };

  const [status, setStatus] = useState(task.status);
  const [assignedTo, setAssignedTo] = useState(task.assignedTo?._id || '');
  const [updating, setUpdating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [maintenanceUsers, setMaintenanceUsers] = useState([]);

  const priorityStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;
  // Anything that is not a clean carries its type beside the title (config/taskTypes.js).
  const typeBadge = taskTypeBadge(task.type);

  // Re-sync local state when task prop changes (e.g., after parent reload)
  useEffect(() => {
    setStatus(task.status);
    setAssignedTo(task.assignedTo?._id || '');
  }, [task.status, task.assignedTo]);

  useEffect(() => {
    loadMaintenanceUsers();
  }, []);

  const loadMaintenanceUsers = async () => {
    try {
      const response = await userApi.getStaffUsers();
      setMaintenanceUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load maintenance users:', error);
      setMaintenanceUsers([]);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      // `false` = the status did NOT change — the parent opened the guest-question dialog, or the
      // server refused and the parent showed why. The select stays where it was.
      const done = await onStatusUpdate(task._id, newStatus);
      if (done !== false) setStatus(newStatus);
    } catch (error) {
      console.error('Status update failed:', error);
      // A known refusal code in the reader's language; otherwise the server's reason over the
      // generic failure text — a bare "failed" tells ops nothing.
      const refusalKey = taskRefusalKey(error?.response?.data);
      alert(refusalKey ? t(refusalKey) : (error?.response?.data?.error || t('tasks.card.errorStatus')));
    }
    setUpdating(false);
  };

  const handleAssignment = async (newAssignee) => {
    setUpdating(true);
    try {
      await onAssign(task._id, newAssignee);
      setAssignedTo(newAssignee);
    } catch (error) {
      console.error('Assignment failed:', error);
      alert(t('tasks.card.errorAssign'));
    }
    setUpdating(false);
  };

  // One close, one request. This used to call `onStatusUpdate` AND `onComplete`, sending the close
  // twice — which would also have opened the guest-question dialog twice.
  const handleComplete = async () => {
    await handleStatusChange('completed');
  };

  const handleTaskUpdated = (updatedTask) => {
    // Reload task data
    if (onEdit) {
      onEdit(updatedTask);
    }
    setShowEditModal(false);
  };

  return (
    <>
      <Card className="mb-3 border-0 shadow-sm" dir={isArabic ? 'rtl' : 'ltr'}>
        <Card.Header
          className="d-flex justify-content-between align-items-center bg-white"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-1">
              <span className="ms-2" style={{ fontSize: '1.2rem' }}>{priorityStyle.emoji}</span>
              <small className="ms-2 text-muted">{priorityStyle.label}</small>
              {task.isOverdue && (
                <Badge bg="danger" className="ms-2">{t('tasks.card.overdue')}</Badge>
              )}
              <span className="me-auto">
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </div>

            {/* Location Name - Property (PMS) or Service Location */}
            {task.displayLocationName && (
              <div className="fw-bold fs-6 text-primary mb-1">
                📍 {task.displayLocationName}
                {task.displayLocationCode && (
                  <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.7em' }} dir="ltr">{task.displayLocationCode}</span>
                )}
              </div>
            )}

            <div className="d-flex align-items-center flex-wrap fw-semibold">
              <span>{task.title}</span>
              {typeBadge && (
                // Every type but a clean is marked, from one place (config/taskTypes.js). A clean is
                // the majority of every list, and a mark everything carries marks nothing.
                // `marginInlineStart`, not `ms-2`: the RTL Bootstrap build sends that one the wrong
                // way in English (I18N_STRATEGY).
                <span
                  className="badge fw-normal text-white"
                  style={{ backgroundColor: typeBadge.color, fontSize: '0.7em', marginInlineStart: '0.5rem' }}
                  title={t(`tasks.type.${task.type}`)}
                >
                  {typeBadge.icon} {t(`tasks.type.${task.type}`)}
                </span>
              )}
              {task.taskSource === 'guest' && (
                // Plain span (not <Badge>) so Bootstrap's bg-primary
                // !important rule doesn't override the brand-purple color.
                <span
                  className="badge ms-2 fw-normal text-white"
                  style={{ backgroundColor: '#471396', fontSize: '0.7em' }}
                  title={t('tasks.guestRequestBadge')}
                >
                  <i className="bi bi-chat-left-quote me-1"></i>
                  {t('tasks.guestRequestBadge')}
                </span>
              )}
            </div>
            {task.ticketId && (
              <div className="small text-primary">{t('tasks.card.ticketRef')}: {task.ticketId}</div>
            )}
            {task.booking?.confirmationCode && (
              <div className="small text-info">{t('tasks.card.bookingRef')}: {task.booking.confirmationCode}</div>
            )}

            {/* Quick Info When Collapsed */}
            {!isExpanded && (
              <div className="small text-muted mt-1">
                {formatDate(task.scheduledDate, {}, language)}
                {task.scheduledTime && ` • ${task.scheduledTime}`}
                {task.assignedTo && ` • ${task.assignedTo.name || task.assignedTo}`}
                {!task.assignedTo && ` • ${t('tasks.card.unassigned')}`}
              </div>
            )}
          </div>
        </Card.Header>

        <Collapse in={isExpanded}>
          <Card.Body>
            {/* Property Address (if available) */}
            {task.property?.address && (
              <div className="mb-3 text-end">
                <small className="text-muted">{t('tasks.card.address')}:</small>
                <div className="small">{task.property.address}</div>
              </div>
            )}

            {/* Booking Confirmation Code */}
            {task.booking?.confirmationCode && (
              <div className="mb-3 text-end">
                <small className="text-muted">{t('tasks.card.bookingRef')}:</small>
                <div className="small text-info fw-semibold">{task.booking.confirmationCode}</div>
              </div>
            )}

        {/* Booking Context */}
        <Row className="mb-2">
          <Col sm={6}>
            <small className="text-muted">{t('tasks.card.checkout')}:</small><br/>
            <strong>
              {formatDate(task.scheduledDate, {}, language)}
              {task.checkoutTime && ` ${task.checkoutTime}`}
            </strong>
          </Col>
          <Col sm={6}>
            <small className="text-muted">{t('tasks.card.checkin')}:</small><br/>
            <strong>{task.checkinTime || t('tasks.card.checkinUnset')}</strong>
          </Col>
        </Row>

        {/* Guest checkout — directly under the SCHEDULED checkout above, on purpose. Those two
            answer different questions and are easy to confuse: «المغادرة» is when policy said the
            guest may leave; this is when they actually did. Reading them together is what turns an
            overdue cleaning row from ambiguous into actionable (D43). */}
        {task.guestCheckout && (
          <GuestCheckoutLine checkout={task.guestCheckout} compact className="small mb-2" />
        )}

        {task.guestName && (
          <div className="mb-2 text-end">
            <small className="text-muted">{t('tasks.card.guest')}:</small> <strong>{task.guestName}</strong>
          </div>
        )}

        {/* Turnover Window Alert */}
        {task.timeWindowHours && task.priority === 'critical' && (
          <Alert variant="warning" className="py-2 px-2 mb-3 text-end">
            <strong>⏱️ {t('tasks.card.turnoverAlert')}: {task.timeWindowHours} {t('tasks.card.turnoverHours')}</strong>
            <div><small>{t('tasks.card.turnoverUrgent')}</small></div>
          </Alert>
        )}

        {/* Status & Assignment Controls */}
        <Row className="mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label className="small">{t('tasks.card.statusLabel')}</Form.Label>
              <Form.Select
                size="sm"
                className="touch-select"
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={updating || status === 'completed'}
                dir={isArabic ? 'rtl' : 'ltr'}
              >
                <option value="pending">{STATUS_LABELS.pending}</option>
                <option value="assigned">{STATUS_LABELS.assigned}</option>
                <option value="in-progress">{STATUS_LABELS['in-progress']}</option>
                <option value="completed">{STATUS_LABELS.completed}</option>
                <option value="cancelled">{STATUS_LABELS.cancelled}</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label className="small">{t('tasks.card.assignedToLabel')}</Form.Label>
              <Form.Select
                size="sm"
                className="touch-select"
                value={assignedTo}
                onChange={(e) => handleAssignment(e.target.value)}
                disabled={updating || status === 'completed'}
                dir={isArabic ? 'rtl' : 'ltr'}
              >
                <option value="">{t('tasks.card.unassigned')}</option>
                {maintenanceUsers.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Guest Update */}
        {task.guestUpdate && (
          <div className="mb-3 text-end">
            <small className="text-muted">{t('tasks.card.guestMessage')}:</small><br/>
            <div className="p-2 bg-info bg-opacity-10 border border-info rounded">
              <small className="text-dark">{task.guestUpdate}</small>
            </div>
          </div>
        )}

        {/* Internal Notes */}
        {task.notes && (
          <div className="mb-3 text-end">
            <small className="text-muted">{t('tasks.card.internalNotes')}:</small><br/>
            <small className="p-2 bg-light rounded d-block">{task.notes}</small>
          </div>
        )}

        {/* Completion Info */}
        {task.status === 'completed' && task.completedAt && (
          <div className="mb-3 p-2 bg-success bg-opacity-10 rounded text-end">
            <small className="text-success">
              ✓ {t('tasks.card.completedAt')} {formatDate(task.completedAt, {}, language)}
              {task.completedBy && ` ${t('tasks.card.completedBy')} ${task.completedBy}`}
            </small>
          </div>
        )}

        {/* Field Team Report (read-only for ops) */}
        {task.status === 'completed' && (task.completionNotes || task.completionRating || task.guestAssessment) && (
          <div className="mb-3 p-2 rounded text-end" style={{ background: '#e8f5e9', borderRight: '3px solid #4CAF50' }}>
            <small className="text-muted d-block mb-1">{t('tasks.card.fieldTeamReport')}</small>
            <GuestAssessmentSummary assessment={task.guestAssessment} />
            {task.completionRating && (
              <div className="mb-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <FaStar key={star} style={{ color: star <= task.completionRating ? '#FFC107' : '#dee2e6', fontSize: '1rem' }} />
                ))}
              </div>
            )}
            {task.completionNotes && (
              <small className="d-block">{task.completionNotes}</small>
            )}
          </div>
        )}

            {/* Action Buttons */}
            <div className="d-flex gap-2">
              {canEditTask && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="touch-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditModal(true);
                  }}
                  disabled={status === 'completed' || status === 'cancelled'}
                >
                  <FaEdit className="ms-1" /> {t('tasks.card.editButton')}
                </Button>
              )}
              <Button
                size="sm"
                variant="success"
                className="touch-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleComplete();
                }}
                disabled={status === 'completed' || updating}
              >
                ✓ {t('tasks.card.completeButton')}
              </Button>
            </div>
          </Card.Body>
        </Collapse>
      </Card>

      {/* Edit Task Modal */}
      <EditTaskModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        task={task}
        onTaskUpdated={handleTaskUpdated}
      />
    </>
  );
};

export default TaskCard;
